import { Component, EventEmitter, Input, Output } from '@angular/core';
import {
  ChatEurostarFare,
  ChatEurostarResultCard,
  RuleActionExtension
} from '@sabstravtech/obtservices/base';
import { Router } from '@angular/router';
import { formatCardCurrency, policyStatusClass, policyStatusLabel } from '../chat-card.util';
import { faChevronDown, faChevronUp } from '@fortawesome/free-solid-svg-icons';

export interface ChatEurostarFareSelection {
  cardIndex: number;
  classType: string;
}

@Component({
  selector: 'app-chat-eurostar-card',
  standalone: false,
  templateUrl: './chat-eurostar-card.component.html',
  styleUrls: ['./chat-eurostar-card.component.scss']
})
export class ChatEurostarCardComponent {
  @Input() card!: ChatEurostarResultCard;
  /** Departure station name (from the leg parameters). */
  @Input() from = '';
  /** Arrival station name (from the leg parameters). */
  @Input() to = '';
  /** Leg date label, e.g. "Thu 4 Jun". */
  @Input() departDate = '';
  @Output() fareSelect = new EventEmitter<ChatEurostarFareSelection>();

  readonly fareOptionsLabel = $localize`:@@chatEurostarFareOptionsAria:View fare options for`;

  faChevronDown = faChevronDown;
  faChevronUp = faChevronUp;

  isExpanded = false;

  get cheapestFare(): ChatEurostarFare | null {
    const available = this.card.fares?.filter((f) => !f.unavailable) ?? [];
    if (!available.length) {
      return null;
    }
    return available.reduce((a, b) => (a.price <= b.price ? a : b));
  }

  get isBlocked(): boolean {
    return !this.cheapestFare;
  }

  get policyClass(): string {
    const fare = this.cheapestFare;
    if (!fare) {
      return 'policy-block';
    }
    return policyStatusClass(fare.policyStatus);
  }

  get policyLabel(): string {
    return policyStatusLabel(this.policyClass);
  }

  /** Cheapest fare's policy fields, shaped for `app-warning-tags` — the same
   *  indicator/tooltip component the results page uses, so the chat card and
   *  results page render identically instead of a bespoke text box. */
  get headlineWarningTicket(): RuleActionExtension {
    const fare = this.cheapestFare;
    return {
      unavailable: !!fare?.unavailable,
      unavailableMessage: fare?.unavailableMessage || '',
      requiresReasonKeys: fare?.requiresReasonKeys || [],
      requiresReasonMessages: fare?.requiresReasonMessages || []
    };
  }

  /** Per-fare policy fields for the expanded "Available fares" list, shaped
   *  for `app-warning-tags` — a fare option can be out of policy independently
   *  of the headline card's cheapest fare. */
  warningTicketFor(fare: ChatEurostarFare): RuleActionExtension {
    return {
      unavailable: !!fare.unavailable,
      unavailableMessage: fare.unavailableMessage || '',
      requiresReasonKeys: fare.requiresReasonKeys || [],
      requiresReasonMessages: fare.requiresReasonMessages || []
    };
  }

  formatPrice(amount: number, currency: string): string {
    return formatCardCurrency(amount, currency);
  }

  onSelectFare(fare: ChatEurostarFare, event: Event): void {
    event.stopPropagation();
    if (fare.unavailable || !fare.classType) {
      return;
    }
    this.fareSelect.emit({ cardIndex: this.card.index, classType: fare.classType });
  }

  toggleExpand(): void {
    if (this.isBlocked) {
      return;
    }
    this.isExpanded = !this.isExpanded;
  }

  onKey(event: KeyboardEvent): void {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.toggleExpand();
    }
  }
}
